import type { FilaDeCartera } from '@/api/cartera.api';
import { haceCuanto, hora12 } from './athena';
import { fechaLegible, legible } from './cartera';
import { ZONA_DEL_NEGOCIO } from './zona';

/**
 * EL PROCESO DE VENTA COMO UNA LÍNEA (tanda 6): en qué paso está cada cliente, qué sigue
 * y cuánto le falta para cerrar. PURO: lo pinta `components/cartera/TarjetaDeProgreso.tsx`.
 *
 * Los pasos son las etapas que el negocio tiene configuradas en su ficha y, al final,
 * «cerrado». Cerrar no es una etapa de la ficha: es la temperatura CERRADO de su chat,
 * y manda sobre la etapa que tenga escrita.
 */

export const CERRADO = 'cerrado';

export interface Paso {
  id: string;
  etiqueta: string;
  /** Nombre del icono de lucide; la tarjeta lo traduce al componente. */
  icono: string;
  sigue: string | null;
}

/** Lo que se sabe de cada etapa. Una que no esté aquí (la agregaron en la configuración) sale con `Circle` y sin «qué sigue». */
const CONOCIDAS: Record<string, { icono: string; sigue: string }> = {
  // Las etapas de Enrique, las mismas que ya usa su panel web (`evento_interesados`).
  nuevo: { icono: 'UserPlus', sigue: 'Preguntar si vienen en pareja y cómo están' },
  interesado: { icono: 'ClipboardCheck', sigue: 'Apuntarlos a la masterclass' },
  contactado: { icono: 'CalendarClock', sigue: 'Confirmar que entran a la masterclass' },
  asistio: { icono: 'Video', sigue: 'Llamarlos al día siguiente y ofrecer la entrada' },
  no_asistio: { icono: 'CalendarX', sigue: 'Ofrecerles la próxima masterclass' },
  aparto: { icono: 'KeyRound', sigue: 'Cobrar el resto de la entrada' },
  pago: { icono: 'Trophy', sigue: 'Mandarles los datos del evento del 29' },
  cancelo: { icono: 'Ban', sigue: 'Dejar la puerta abierta para la próxima' },
  [CERRADO]: { icono: 'Trophy', sigue: 'Después del evento: el libro, el reto y los retiros' },
};

/** Lo que toca hacer con un cliente que está en esa etapa, o null si la etapa no es de las conocidas. */
export function queSigue(etapa: string): string | null {
  return CONOCIDAS[etapa]?.sigue ?? null;
}

/** Los pasos del negocio: las etapas configuradas, en su orden, y al final «cerrado» (una sola vez). */
export function pasosDe(etapas: readonly string[]): Paso[] {
  const ids = [...new Set([...etapas.filter((e) => e && e !== CERRADO), CERRADO])];
  return ids.map((id) => ({ id, etiqueta: legible(id), icono: CONOCIDAS[id]?.icono ?? 'Circle', sigue: queSigue(id) }));
}

const estaCerrado = (fila: FilaDeCartera) => fila.conversacion?.temperatura === 'CERRADO';

/** Índice del paso en que está. CERRADO (temperatura) → el último. Sin etapa o desconocida → 0. */
export function posicionDe(fila: FilaDeCartera, etapas: readonly string[]): number {
  const pasos = pasosDe(etapas);
  if (estaCerrado(fila)) return pasos.length - 1;
  return Math.max(0, pasos.findIndex((p) => p.id === fila.etapa));
}

/** Cuántos pasos le faltan para cerrar (0 si ya cerró). */
export function faltan(fila: FilaDeCartera, etapas: readonly string[]): number {
  return pasosDe(etapas).length - 1 - posicionDe(fila, etapas);
}

// El equipo trabaja en la hora del negocio: el panel la muestra así aunque se abra desde otro país.
const ZONA = ZONA_DEL_NEGOCIO;
const DIA_MS = 86_400_000;
const PARTES = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONA,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hourCycle: 'h23',
});

/**
 * La misma fecha con sus campos LOCALES puestos a la hora de pared del negocio. `hora12` lee
 * la hora local del navegador: con esto dice la del negocio, y contar días entre dos de estas
 * fechas da «hoy» y «mañana» de allá.
 */
function enLaDelNegocio(fecha: Date): Date {
  const p = Object.fromEntries(PARTES.formatToParts(fecha).map((x) => [x.type, Number(x.value)]));
  return new Date(p.year!, p.month! - 1, p.day!, p.hour! % 24, p.minute!);
}

const medianoche = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** «hoy 4:00 p. m.», «mañana 11:00 a. m.», «12 oct 9:30 a. m.». */
function cuando(iso: string, ahora: Date): string {
  const fecha = enLaDelNegocio(new Date(iso));
  const dias = Math.round((medianoche(fecha) - medianoche(enLaDelNegocio(ahora))) / DIA_MS);
  const dia = dias === 0 ? 'hoy' : dias === 1 ? 'mañana' : fechaLegible(iso).replace(/ \d{4}$/, '');
  return `${dia} ${hora12(fecha)}`;
}

/** «hace 2 días» → «hace 2 d»: en la tarjeta el plazo va en una esquina. */
const hace = (iso: string, ahora: Date) => haceCuanto(iso, ahora).replace(/ días?$/, ' d');

const recortar = (texto: string, largo: number) => (texto.length > largo ? `${texto.slice(0, largo - 1).trimEnd()}…` : texto);

export interface Plazo {
  texto: string;
  tono: 'normal' | 'alerta' | 'apagado';
}

/**
 * Lo que va donde la captura dice «ETA». Por orden: ya cerró; la masterclass si está
 * apuntado a una (etapa `contactado`); la próxima acción con fecha (o cuánto lleva
 * vencida); la próxima acción sin fecha; nada.
 */
export function plazoDe(fila: FilaDeCartera, ahora: Date): Plazo {
  if (estaCerrado(fila)) return { texto: 'Cerrado', tono: 'normal' };
  // `contactado` es la etapa en la que la pareja ya tiene su lugar apartado en una
  // masterclass; por dentro sigue siendo la misma cita por Zoom.
  if (fila.etapa === 'contactado' && fila.zoomEl) {
    // La API manda la más cercana; si ya pasaron todas, la última: toca marcarla como hecha.
    return Date.parse(fila.zoomEl) >= ahora.getTime()
      ? { texto: `Masterclass: ${cuando(fila.zoomEl, ahora)}`, tono: 'normal' }
      : { texto: `La masterclass fue ${hace(fila.zoomEl, ahora)}`, tono: 'alerta' };
  }
  const proxima = fila.proximaAccion;
  if (proxima?.fecha) {
    return Date.parse(proxima.fecha) >= ahora.getTime()
      ? { texto: `Próximo: ${cuando(proxima.fecha, ahora)}`, tono: 'normal' }
      : { texto: `Vencido ${hace(proxima.fecha, ahora)}`, tono: 'alerta' };
  }
  if (proxima) return { texto: `Próximo: ${recortar(proxima.descripcion.trim(), 28)}`, tono: 'normal' };
  return { texto: 'Sin próxima acción', tono: 'apagado' };
}

/**
 * «hace 3 d en esta etapa», o null si no se sabe desde cuándo. De un cliente cerrado
 * tampoco se sabe: el sello es de la última etapa que se le puso a mano, no del cierre.
 */
export function enEtapaDesde(fila: FilaDeCartera, ahora: Date): string | null {
  if (!fila.etapaDesde || estaCerrado(fila)) return null;
  const t = hace(fila.etapaDesde, ahora);
  return t === 'ahora mismo' ? 'recién llegó a esta etapa' : `${t} en esta etapa`;
}

/** Cómo se nombra cada entrada en la tarjeta. */
const ENTRADA: Record<string, string> = { gold: 'Gold', platinum: 'Platinum', diamond: 'Diamante' };

/** «diamond» → «Entrada Diamante». Lo que no es una de las tres se deja como lo escribieron. */
export function entradaCorta(crudo: string | undefined): string | null {
  const texto = crudo?.trim();
  if (!texto) return null;
  return ENTRADA[texto.toLowerCase()] ? `Entrada ${ENTRADA[texto.toLowerCase()]}` : texto;
}

/**
 * «9 anos» → «9 años juntos»; «casi un ano» → «casi un año juntos».
 *
 * El extractor guarda lo que dijeron sin acentos (pasa por `fold`), así que la ñ se
 * repone aquí. Lo que no nombra una unidad de tiempo se deja tal cual: es lo que el
 * cliente escribió y no se adivina.
 */
export function tiempoCorto(crudo: string | undefined): string | null {
  const texto = crudo?.trim();
  if (!texto) return null;
  if (!/\b(anos?|anitos?|meses|mes|semanas?)\b/i.test(texto)) return texto;
  return `${texto.replace(/\banos\b/i, 'años').replace(/\bano\b/i, 'año')} juntos`;
}

/**
 * Los dos datos en gris junto al nombre: la entrada que miran y cuánto llevan juntos; si
 * faltan, la ciudad, el tipo y el país. Máximo dos.
 */
export function datosDe(fila: FilaDeCartera): string[] {
  return [
    entradaCorta(fila.ficha.entrada_que_mira),
    tiempoCorto(fila.ficha.tiempo_juntos),
    fila.ficha.ciudad?.trim(),
    fila.tipo && legible(fila.tipo),
    fila.pais,
  ]
    .filter((d): d is string => Boolean(d))
    .slice(0, 2);
}
